import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PackagePlus, Search, Download, CheckSquare } from 'lucide-react';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../context/ToastContext';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'CONFIRMED', 'DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'];
const LIMIT = 20;
const BASE = '/company-admin/shipments';

export const CompanyAdminShipments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [shipments, setShipments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [receiverSearch, setReceiverSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'confirm' | 'cancel' | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.trackingNumber = search;
      if (receiverSearch) filters.receiverName = receiverSearch;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.listShipments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setShipments(d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, search, receiverSearch, statusFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (id: string) => { await apiClient.confirmShipment(id); load(); };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === shipments.length) setSelected(new Set());
    else setSelected(new Set(shipments.map(s => s.id)));
  };

  const executeBulk = async () => {
    if (!bulkAction) return;
    try {
      await apiClient.bulkShipmentAction(Array.from(selected), bulkAction);
      toast(`Bulk ${bulkAction} applied to ${selected.size} shipments`, 'success');
      setSelected(new Set());
      load();
    } catch (e: any) {
      toast(e.response?.data?.error || 'Bulk action failed', 'error');
    } finally { setBulkAction(null); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.exportShipmentsCsv(filters);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `shipments-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast('Export downloaded', 'success');
    } catch { toast('Export failed', 'error'); }
    finally { setExporting(false); }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const bulkConfirmable = shipments.filter(s => selected.has(s.id) && s.status === 'PENDING');
  const bulkCancellable = shipments.filter(s => selected.has(s.id) && ['PENDING', 'CONFIRMED'].includes(s.status));

  return (
    <>
      {bulkAction && (
        <ConfirmModal
          title={`Bulk ${bulkAction}`}
          message={`Apply "${bulkAction}" to ${selected.size} selected shipment(s)?`}
          confirmLabel={bulkAction === 'cancel' ? 'Cancel Shipments' : 'Confirm Shipments'}
          danger={bulkAction === 'cancel'}
          onConfirm={executeBulk}
          onCancel={() => setBulkAction(null)}
        />
      )}

      <div className="page-header">
        <h1 className="page-title">My Delivery Orders</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={exportCsv} disabled={exporting}>
            <Download size={15} /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <Link to={`${BASE}/create`} className="btn btn-primary"><PackagePlus size={16} /> New Shipment</Link>
        </div>
      </div>

      <div className="card">
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Tracking number..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Receiver name..." value={receiverSearch} onChange={e => { setReceiverSearch(e.target.value); setPage(0); }} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" style={{ width: 145 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" style={{ width: 145 }} />
        </div>

        {/* Bulk actions bar */}
        {selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, marginBottom: 10, fontSize: 13 }}>
            <CheckSquare size={15} color="#3b82f6" />
            <span style={{ fontWeight: 600, color: '#1d4ed8' }}>{selected.size} selected</span>
            {bulkConfirmable.length > 0 && (
              <button className="btn btn-success" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => setBulkAction('confirm')}>
                Confirm {bulkConfirmable.length}
              </button>
            )}
            {bulkCancellable.length > 0 && (
              <button className="btn btn-danger" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => setBulkAction('cancel')}>
                Cancel {bulkCancellable.length}
              </button>
            )}
            <button className="btn btn-ghost" style={{ padding: '3px 10px', fontSize: 12, marginLeft: 'auto' }} onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        {loading ? <div className="loading-spinner">Loading...</div> : shipments.length === 0 ? (
          <div className="empty-state"><h3>No shipments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input type="checkbox" checked={selected.size === shipments.length && shipments.length > 0} onChange={toggleAll} />
                    </th>
                    <th>Tracking #</th><th>Sender</th><th>Receiver</th><th>City</th><th>Status</th><th>Amount</th><th>Source</th><th>Date</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s: any) => (
                    <tr key={s.id} style={{ background: selected.has(s.id) ? '#f0f9ff' : undefined }}>
                      <td><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} /></td>
                      <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                      <td>{s.senderName}</td>
                      <td>{s.receiverName}</td>
                      <td>{s.receiverCity || '—'}</td>
                      <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{Number(s.totalAmount).toLocaleString()} RWF</td>
                      <td>
                        {s.senderId === null
                          ? <span style={{ fontSize: 11, background: '#eff6ff', color: '#3b82f6', padding: '2px 7px', borderRadius: 10, fontWeight: 500 }}>API</span>
                          : <span style={{ fontSize: 11, color: '#94a3b8' }}>Manual</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <Link to={`${BASE}/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>
                        {['PENDING', 'CONFIRMED'].includes(s.status) && (
                          <Link to={`${BASE}/${s.id}/edit`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>Edit</Link>
                        )}
                        {s.status === 'PENDING' && (
                          <button className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => confirm(s.id)}>Confirm</button>
                        )}
                        {['DEPARTED', 'IN_TRANSIT'].includes(s.status) && (
                          <Link to={`${BASE}/${s.id}/deliver`} className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }}>Deliver</Link>
                        )}
                        {s.status === 'CONFIRMED' && (
                          <Link to={`${BASE}/${s.id}/dispatch`} className="btn btn-primary" style={{ padding: '3px 8px', fontSize: 12 }}>Dispatch</Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
